import { useCallback, useLayoutEffect, useRef, useState } from "react";

export function useTicketScroll(taskId: number, messageCount: number, loading: boolean) {
    const messagesRef = useRef<HTMLDivElement>(null);
    const followRef = useRef(true);
    const previousCountRef = useRef(0);
    const [hasNewMessages, setHasNewMessages] = useState(false);

    const scrollToLatest = useCallback(() => {
        const container = messagesRef.current;
        followRef.current = true;
        setHasNewMessages(false);
        if (container) container.scrollTop = container.scrollHeight;
    }, []);

    useLayoutEffect(() => {
        followRef.current = true;
        previousCountRef.current = 0;
        setHasNewMessages(false);
    }, [taskId]);

    useLayoutEffect(() => {
        if (loading) return;
        if (followRef.current) scrollToLatest();
        else if (messageCount > previousCountRef.current) setHasNewMessages(true);
        previousCountRef.current = messageCount;
    }, [messageCount, loading, scrollToLatest]);

    // Keep the bottom visible through keyboard/rating/composer size changes,
    // while retaining the reader's position when they have scrolled up.
    useLayoutEffect(() => {
        const container = messagesRef.current;
        if (!container || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(() => {
            if (followRef.current) scrollToLatest();
        });
        observer.observe(container);
        return () => observer.disconnect();
    }, [scrollToLatest]);

    const onMessagesScroll = useCallback(() => {
        const container = messagesRef.current;
        if (!container) return;
        const nearBottom = container.scrollHeight - container.clientHeight - container.scrollTop <= 64;
        followRef.current = nearBottom;
        if (nearBottom) setHasNewMessages(false);
    }, []);

    return { messagesRef, onMessagesScroll, scrollToLatest, hasNewMessages };
}
